import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Boxes,
  Flame,
  Share2,
  RefreshCcw,
  CheckCircle2,
  XCircle,
  Trophy,
} from "@/lib/icons";
import AppHeader from "@/components/AppHeader";
import Main from "@/components/Main";
import { Button } from "@/components/ui/button";
import { categoryLabels } from "@/data/elements";
import {
  buildShareText,
  getDailyPuzzle,
  loadResult,
  loadStreak,
  saveResult,
  todayKey,
  type DailyPuzzle,
  type DailyResult,
} from "@/games/dailyElement/engine";
import { useToast } from "@/hooks/use-toast";
import Seo from "@/components/Seo";

/**
 * /daily - Element of the Day
 *
 * Wordle-style daily challenge:
 *  - Same element + same questions for everyone on the same calendar day.
 *  - One play per day; result is locked after submitting.
 *  - Streak counter persists in localStorage; share text uses emoji grid.
 *
 * Educational reveal: the element fact card unlocks after the player
 * commits their answers, so the questions can't be answered just by
 * reading the card first.
 */
export default function DailyElement() {
  const { toast } = useToast();

  // Recompute the puzzle once per mount; today's key is stable for a session.
  const dateKey = useMemo(() => todayKey(), []);
  const puzzle: DailyPuzzle = useMemo(() => getDailyPuzzle(dateKey), [dateKey]);

  const [answers, setAnswers] = useState<number[]>(() =>
    puzzle.questions.map(() => -1),
  );
  const [result, setResult] = useState<DailyResult | null>(null);
  const [streak, setStreak] = useState<{ current: number; best: number }>({
    current: 0,
    best: 0,
  });

  // Hydrate persisted result on mount (post-SSR).
  useEffect(() => {
    const existing = loadResult(dateKey);
    if (existing) {
      setResult(existing);
      setAnswers(existing.answers);
    }
    setStreak(loadStreak());
  }, [dateKey]);

  const allAnswered = answers.every((a) => a >= 0);
  const locked = !!result;

  function handlePick(qIdx: number, optIdx: number) {
    if (locked) return;
    setAnswers((prev) => {
      const next = prev.slice();
      next[qIdx] = optIdx;
      return next;
    });
  }

  function handleSubmit() {
    if (!allAnswered || locked) return;
    const score = puzzle.questions.reduce(
      (s, q, i) => (answers[i] === q.correctIndex ? s + 1 : s),
      0,
    );
    const next: DailyResult = {
      dateKey,
      answers,
      score,
      total: puzzle.questions.length,
      completedAt: new Date().toISOString(),
    };
    const updated = saveResult(next);
    setResult(next);
    setStreak(updated);
  }

  async function handleShare() {
    if (!result) return;
    const text = buildShareText(puzzle, result);
    try {
      if (typeof navigator !== "undefined" && (navigator as Navigator).share) {
        await (navigator as Navigator).share({ text });
        return;
      }
      await navigator.clipboard.writeText(text);
      toast({ title: "Copied!", description: "Share text copied to your clipboard." });
    } catch {
      toast({
        title: "Couldn't share",
        description: "Try copying the text manually below.",
      });
    }
  }

  const el = puzzle.element;

  return (
    <div className="min-h-screen bg-transparent">
      <Seo title="Daily Element Challenge — ElementPad" description="A new periodic table element to learn every day. Quick facts, trivia, and a daily quiz." path="/daily" />
      <AppHeader />
      <Main className="max-w-[760px] mx-auto px-4 py-6">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-4 transition-colors"
        >
          <ArrowLeft size={12} aria-hidden="true" />
          Back home
        </Link>

        <header className="mb-6 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Boxes size={14} className="text-primary" aria-hidden="true" />
              <span className="text-[11px] font-semibold uppercase tracking-wider text-primary">
                Element of the Day
              </span>
            </div>
            <h1 className="text-2xl font-bold text-foreground">
              {dateKey}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Same puzzle for everyone today. One play - then come back tomorrow.
            </p>
          </div>
          <div className="flex-shrink-0 text-right">
            <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-border bg-card text-xs font-medium">
              <Flame
                size={12}
                className="text-orange-500 dark:text-orange-400"
                aria-hidden="true"
              />
              <span className="font-mono">{streak.current}</span>
              <span className="text-muted-foreground">streak</span>
            </div>
            {streak.best > 0 && (
              <div className="text-[10px] text-muted-foreground mt-1">
                Best: {streak.best}
              </div>
            )}
          </div>
        </header>

        {/* Element reveal - hidden until the puzzle is locked in. */}
        {locked && (
          <section
            className="mb-6 p-5 rounded-xl border border-primary/30 bg-primary/5"
            aria-labelledby="todays-element"
          >
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-20 h-20 rounded-lg border border-primary/40 bg-card flex flex-col items-center justify-center font-mono">
                <span className="text-[10px] text-muted-foreground leading-none">
                  {el.number}
                </span>
                <span className="text-3xl font-bold text-primary leading-tight">
                  {el.symbol}
                </span>
                <span className="text-[9px] text-muted-foreground leading-none mt-0.5">
                  {el.mass}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <h2 id="todays-element" className="text-xl font-bold text-foreground">
                  {el.name}
                </h2>
                <div className="text-xs text-muted-foreground mb-2">
                  {categoryLabels[el.category]} - Period {el.period}
                  {el.group ? ` - Group ${el.group}` : ""}
                </div>
                <p className="text-sm text-foreground leading-relaxed">
                  {puzzle.fact.fact}
                </p>
                {puzzle.fact.didYouKnow && (
                  <p className="text-xs text-muted-foreground leading-relaxed mt-2">
                    <span className="font-semibold text-foreground">Did you know? </span>
                    {puzzle.fact.didYouKnow}
                  </p>
                )}
              </div>
            </div>
          </section>
        )}

        {/* Questions */}
        <section aria-label="Today's questions" className="space-y-4">
          {puzzle.questions.map((q, qIdx) => {
            const picked = answers[qIdx];
            return (
              <div
                key={q.id}
                className="p-4 rounded-xl border border-border bg-card"
              >
                <div className="flex items-baseline gap-2 mb-3">
                  <span className="text-[10px] font-mono font-semibold text-muted-foreground">
                    {qIdx + 1}/{puzzle.questions.length}
                  </span>
                  <h3 className="text-sm font-semibold text-foreground">
                    {q.prompt}
                  </h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {q.options.map((opt, oIdx) => {
                    const isPicked = picked === oIdx;
                    const isCorrect = oIdx === q.correctIndex;
                    let stateClass =
                      "border-border bg-background hover:border-primary/40 hover:bg-accent/30";
                    if (locked) {
                      if (isCorrect) {
                        stateClass =
                          "border-emerald-500/50 bg-emerald-500/10 text-foreground";
                      } else if (isPicked) {
                        stateClass =
                          "border-destructive/50 bg-destructive/10 text-foreground";
                      } else {
                        stateClass = "border-border bg-background opacity-60";
                      }
                    } else if (isPicked) {
                      stateClass = "border-primary bg-primary/10 text-foreground";
                    }
                    return (
                      <button
                        key={oIdx}
                        type="button"
                        disabled={locked}
                        onClick={() => handlePick(qIdx, oIdx)}
                        className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg border text-sm text-left transition-colors ${stateClass} disabled:cursor-default`}
                      >
                        <span className="font-mono">{opt}</span>
                        {locked && isCorrect && (
                          <CheckCircle2
                            size={14}
                            className="text-emerald-500 flex-shrink-0"
                            aria-label="Correct answer"
                          />
                        )}
                        {locked && isPicked && !isCorrect && (
                          <XCircle
                            size={14}
                            className="text-destructive flex-shrink-0"
                            aria-label="Your incorrect answer"
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
                {locked && q.explanation && (
                  <p className="text-xs text-muted-foreground mt-2">{q.explanation}</p>
                )}
              </div>
            );
          })}
        </section>

        {/* Action row */}
        <div className="mt-6 flex flex-wrap items-center gap-3">
          {!locked && (
            <Button
              onClick={handleSubmit}
              disabled={!allAnswered}
              className="font-medium"
            >
              Submit answers
            </Button>
          )}
          {locked && (
            <>
              <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-card text-sm">
                <Trophy size={14} className="text-primary" aria-hidden="true" />
                <span className="font-semibold">
                  {result?.score}/{result?.total} correct
                </span>
              </div>
              <Button onClick={handleShare} variant="default" className="font-medium">
                <Share2 size={14} className="mr-1.5" aria-hidden="true" />
                Share result
              </Button>
              <Button asChild variant="ghost" size="sm">
                <Link to="/play">
                  <RefreshCcw size={12} className="mr-1" aria-hidden="true" />
                  Try another game
                </Link>
              </Button>
            </>
          )}
        </div>

        {locked && (
          <p className="text-[11px] text-muted-foreground mt-4">
            New element drops at midnight your local time.
          </p>
        )}
      </Main>
    </div>
  );
}